import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Grid,
  Theme,
  Autocomplete,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';

import { postData, fetchData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { questionRestrictionOptions } from '../../constants/questionOptions';
import { WAS_QUESTION_TYPE_MAP } from '../../types/questionTypeMap';

interface ProductCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

interface VendorData {
  id: string;
  vendor: string;
  questionRestriction: string;
  questionType: string;
}

interface AssigneeData {
  id: string;
  assignee: string;
  assigneeId: any;
  questionRestriction: string;
  questionType: string;
}

interface WPDCProduct {
  title: string;
  id?: string;
  season?: string;
  discipline?: string;
  uuid?: string;
  eprof?: string;
  isbn?: string;
}

interface UserOption {
  id: string;
  name: string;
  userId: string;
  role: string;
}

const ProductCreateDialog: React.FC<ProductCreateDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {

  const [vendors, setVendors] = useState<VendorData[]>([]);

  const [assignees, setAssignees] = useState<AssigneeData[]>([]);

  // Product search states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productOptions, setProductOptions] = useState<WPDCProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<WPDCProduct | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Form data states
  const [formData, setFormData] = useState({
    product: '',
    season: '',
    discipline: '',
    uuid: '',
    eprof: '',
    svnRepository: '',
    rootFolder: '',
    caseOwner: '',
  });

  // Loading and error states for API call
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Track which fields have been assigned values and should be readonly
  const [readonlyFields, setReadonlyFields] = useState({
    season: false,
    discipline: false,
    uuid: false,
    eprof: false,
  });

  // Root folders state
  const [rootFolders, setRootFolders] = useState<string[]>([]);
  const [isLoadingRootFolders, setIsLoadingRootFolders] = useState(false);
  const [rootFoldersError, setRootFoldersError] = useState<string | null>(null);

  // Case owner search states
  const [caseOwnerSearchTerm, setCaseOwnerSearchTerm] = useState('');
  const [caseOwnerOptions, setCaseOwnerOptions] = useState<UserOption[]>([]);
  const [selectedCaseOwner, setSelectedCaseOwner] = useState<UserOption | null>(null);
  const [isSearchingCaseOwner, setIsSearchingCaseOwner] = useState(false);
  const [caseOwnerSearchError, setCaseOwnerSearchError] = useState<string | null>(null);

  // Assignee search states
  const [assigneeSearchTerms, setAssigneeSearchTerms] = useState<{ [key: string]: string }>({});
  const [assigneeOptions, setAssigneeOptions] = useState<{ [key: string]: UserOption[] }>({});
  const [isSearchingAssignee, setIsSearchingAssignee] = useState<{ [key: string]: boolean }>({});

  // Search WPDC products
  const searchWPDCProducts = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProductOptions([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const response = await postData(
        '/wpdc/products/search',
        PERMISSIONS.SEARCH_WPDC_PRODUCTS,
        { title: searchTerm }
      );

      if (response && Array.isArray(response)) {
        setProductOptions(response);
      } else if (response && response.products && Array.isArray(response.products)) {
        setProductOptions(response.products);
      } else {
        setProductOptions([]);
      }
    } catch (error: any) {
      console.error('Error searching WPDC products:', error);
      setSearchError(error.message || 'Failed to search products');
      setProductOptions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Autofill form fields when a product is selected
  const handleProductSelection = (product: WPDCProduct | null) => {
    setSelectedProduct(product);
    
    if (product) {
      // Update the product search term
      setProductSearchTerm(product.title);
      
      // Autofill the form fields with product data
      setFormData(prev => ({
        ...prev,
        product: product.title,
        season: product.season || '',
        discipline: product.discipline || '',
        uuid: product.id || product.uuid || '', // Use id as primary, uuid as fallback
        eprof: product.eprof || product.isbn || '', // Use isbn as fallback for eprof
      }));

      // Mark fields as readonly if they have values
      setReadonlyFields({
        season: !!(product.season),
        discipline: !!(product.discipline),
        uuid: !!(product.id || product.uuid),
        eprof: !!(product.eprof || product.isbn),
      });
    } else {
      // Clear the form fields if no product is selected
      setFormData(prev => ({
        ...prev,
        product: '',
        season: '',
        discipline: '',
        uuid: '',
        eprof: '',
      }));

      // Reset readonly state
      setReadonlyFields({
        season: false,
        discipline: false,
        uuid: false,
        eprof: false,
      });
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (productSearchTerm.trim()) {
        searchWPDCProducts(productSearchTerm);
      } else {
        setProductOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [productSearchTerm]);

  // Fetch root folders from API
  const fetchRootFolders = async () => {
    setIsLoadingRootFolders(true);
    setRootFoldersError(null);

    try {
      const response = await fetchData(
        '/wpdc/root-folders',
        PERMISSIONS.GET_WPDC_ROOT_FOLDERS
      );

      if (response && response.root_folders && Array.isArray(response.root_folders)) {
        setRootFolders(response.root_folders);
      } else {
        setRootFolders([]);
        setRootFoldersError('Invalid response format');
      }
    } catch (error: any) {
      console.error('Error fetching root folders:', error);
      setRootFoldersError(error.message || 'Failed to fetch root folders');
      setRootFolders([]);
    } finally {
      setIsLoadingRootFolders(false);
    }
  };

  // Fetch root folders when component mounts
  useEffect(() => {
    if (open) {
      fetchRootFolders();
    }
  }, [open]);

  // Search users for case owner (filtered to ADMIN role only)
  const searchCaseOwners = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setCaseOwnerOptions([]);
      return;
    }

    setIsSearchingCaseOwner(true);
    setCaseOwnerSearchError(null);

    try {
      const response = await postData(
        '/users/search',
        PERMISSIONS.SEARCH_USERS,
        { searchText: searchTerm }
      );

      let users: UserOption[] = [];
      if (response && Array.isArray(response)) {
        users = response;
      } else if (response && response.users && Array.isArray(response.users)) {
        users = response.users;
      }

      // Filter to only include users with ADMIN role
      const adminUsers = users.filter(user => user.role === 'ADMIN');
      setCaseOwnerOptions(adminUsers);
    } catch (error: any) {
      console.error('Error searching case owners:', error);
      setCaseOwnerSearchError(error.message || 'Failed to search case owners');
      setCaseOwnerOptions([]);
    } finally {
      setIsSearchingCaseOwner(false);
    }
  };

  // Debounced search effect for case owner
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (caseOwnerSearchTerm.trim()) {
        searchCaseOwners(caseOwnerSearchTerm);
      } else {
        setCaseOwnerOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [caseOwnerSearchTerm]);

  // Search users for assignee
  const searchAssignees = async (searchTerm: string, assigneeId: string) => {
    if (!searchTerm.trim()) {
      setAssigneeOptions(prev => ({ ...prev, [assigneeId]: [] }));
      return;
    }

    setIsSearchingAssignee(prev => ({ ...prev, [assigneeId]: true }));

    try {
      const response = await postData(
        '/users/search',
        PERMISSIONS.SEARCH_USERS,
        { searchText: searchTerm }
      );

      if (response && Array.isArray(response)) {
        setAssigneeOptions(prev => ({ ...prev, [assigneeId]: response }));
      } else if (response && response.users && Array.isArray(response.users)) {
        setAssigneeOptions(prev => ({ ...prev, [assigneeId]: response.users }));
      } else {
        setAssigneeOptions(prev => ({ ...prev, [assigneeId]: [] }));
      }
    } catch (error: any) {
      console.error('Error searching assignees:', error);
      setAssigneeOptions(prev => ({ ...prev, [assigneeId]: [] }));
    } finally {
      setIsSearchingAssignee(prev => ({ ...prev, [assigneeId]: false }));
    }
  };

  // Helper function to reset all form data
  const resetFormData = () => {
    setFormData({
      product: '',
      season: '',
      discipline: '',
      uuid: '',
      eprof: '',
      svnRepository: '',
      rootFolder: '',
      caseOwner: '',
    });
    
    setProductSearchTerm('');
    setProductOptions([]);
    setSelectedProduct(null);
    setSearchError(null);
    
    setVendors([]);
    setAssignees([]);
    
    setReadonlyFields({
      season: false,
      discipline: false,
      uuid: false,
      eprof: false,
    });

    // Reset case owner search states
    setCaseOwnerSearchTerm('');
    setCaseOwnerOptions([]);
    setSelectedCaseOwner(null);
    setCaseOwnerSearchError(null);

    // Reset assignee search states
    setAssigneeSearchTerms({});
    setAssigneeOptions({});
    setIsSearchingAssignee({});

    // Reset API call states
    setIsSubmitting(false);
    setSubmitError(null);
  };

  // Reset form data when dialog opens
  useEffect(() => {
    if (open) {
      resetFormData();
    }
  }, [open]);

  const handleAddVendor = () => {
    const newVendor: VendorData = {
      id: Date.now().toString(),
      vendor: '',
      questionRestriction: questionRestrictionOptions[0].value,
      questionType: WAS_QUESTION_TYPE_MAP[0].value.toString(),
    };
    setVendors([...vendors, newVendor]);
  };

  const handleRemoveVendor = (id: string) => {
    setVendors(vendors.filter(vendor => vendor.id !== id));
  };

  const handleAddAssignee = () => {
    const newAssignee: AssigneeData = {
      id: Date.now().toString(),
      assignee: '',
      assigneeId: '',
      questionRestriction: questionRestrictionOptions[0].value,
      questionType: WAS_QUESTION_TYPE_MAP[0].value.toString(),
    };
    setAssignees([...assignees, newAssignee]);
  };

  const handleRemoveAssignee = (id: string) => {
    setAssignees(assignees.filter(assignee => assignee.id !== id));
  };

  const handleClose = (event: any, reason: string) => {
    // Only allow closing via buttons, not backdrop click or escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    
    // Clear all form data when closing
    resetFormData();
    
    onClose();
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.rootFolder || !formData.season || !formData.svnRepository || !formData.product || !formData.discipline || !formData.uuid || !formData.eprof || !formData.caseOwner) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    // Validate vendors and assignees
    if (vendors.length === 0 || assignees.length === 0) {
      setSubmitError('Please add at least one vendor and assignee');
      return;
    }

    // Validate vendor names for special characters
    const vendorsWithSpecialChars = vendors.filter(vendor => 
      vendor.vendor.trim() && hasSpecialCharacters(vendor.vendor)
    );
    if (vendorsWithSpecialChars.length > 0) {
      setSubmitError('Vendor names can only contain letters, numbers, hyphens, underscores, and periods.');
      return;
    }

    // Validate uniqueness of vendor + question restriction + question type combination
    const vendorCombinations = vendors
      .filter(vendor => vendor.vendor.trim() !== '')
      .map(vendor => `${normalizeVendorName(vendor.vendor)}|${normalizeComboValue(vendor.questionRestriction)}|${normalizeComboValue(vendor.questionType)}`);
    const uniqueVendorCombinations = new Set(vendorCombinations);
    if (vendorCombinations.length !== uniqueVendorCombinations.size) {
      setSubmitError('Duplicate vendor, question restriction, and question type combinations are not allowed.');
      return;
    }

    // Validate uniqueness of assignee + question restriction + question type combination
    const assigneeCombinations = assignees
      .filter(assignee => assignee.assignee.trim() !== '')
      .map(assignee => `${normalizeVendorName(assignee.assignee)}|${normalizeComboValue(assignee.questionRestriction)}|${normalizeComboValue(assignee.questionType)}`);
    const uniqueAssigneeCombinations = new Set(assigneeCombinations);
    if (assigneeCombinations.length !== uniqueAssigneeCombinations.size) {
      setSubmitError('Duplicate assignee, question restriction, and question type combinations are not allowed.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate case owner selection
      if (!selectedCaseOwner) {
        throw new Error('Selected case owner not found');
      }

      // Prepare the request body according to the API specification
      const requestBody = {
        rootFolder: formData.rootFolder,
        season: formData.season,
        svnPath: formData.svnRepository,
        title: formData.product,
        discipline: formData.discipline,
        eprof: formData.eprof,
        uuid: formData.uuid,
        caseOwner: selectedCaseOwner.userId,
        vendors: vendors.map(vendor => ({
          vendor: vendor.vendor,
          questionRestriction: vendor.questionRestriction,
          questionType: vendor.questionType
        })),
        assignees: assignees.map(assignee => ({
          assignee: assignee.assignee,
          assigneeId: assignee.assigneeId,
          questionRestriction: assignee.questionRestriction,
          questionType: assignee.questionType
        }))
      };

      // Make the API call
      const response = await postData(
        '/products/add',
        PERMISSIONS.ADD_PRODUCT,
        requestBody
      );

      // Call the parent's onSave callback with the response
      onSave(response);
      
      // Clear all form data after successful save
      resetFormData();
      
      onClose();
    } catch (error: any) {
      console.error('Error creating product:', error);
      setSubmitError(error.message || 'Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear all form data when canceling
    resetFormData();
    onClose();
  };

  const handleFormChange = (field: string, value: string) => {
    // Don't allow changes to readonly fields
    if (readonlyFields[field as keyof typeof readonlyFields]) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as readonly if it gets a value and is one of the target fields
    if (['season', 'discipline', 'uuid', 'eprof'].includes(field) && value.trim()) {
      setReadonlyFields(prev => ({
        ...prev,
        [field]: true
      }));
    }
  };

  // Helper function to normalize vendor name for comparison (remove spaces, convert to lowercase)
  const normalizeVendorName = (name: string) => {
    return name.replace(/\s+/g, '').toLowerCase();
  };

  // Helper function to normalize question restriction/type values for combination checks
  const normalizeComboValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim().toLowerCase();
  };

  // Helper function to check if vendor name contains special characters
  const hasSpecialCharacters = (name: string) => {
    // Allow only letters, numbers, and basic punctuation (hyphens, underscores, periods)
    const allowedPattern = /^[a-zA-Z0-9\-_.\s]+$/;
    return !allowedPattern.test(name);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'white',
        },
      }}
    >
      <DialogTitle sx={{ marginY: 1 }}>
        <Typography variant="h5">
          Product Onboarding
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Product Information Section */}
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  options={productOptions}
                  getOptionLabel={(option) => 
                    typeof option === 'string' ? option : option.title
                  }
                  inputValue={productSearchTerm}
                  onInputChange={(event, newInputValue) => {
                    setProductSearchTerm(newInputValue);
                    // Only update product field, don't autofill others until selection
                    handleFormChange('product', newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      handleProductSelection(newValue);
                    } else {
                      handleProductSelection(null);
                    }
                  }}
                  loading={isSearching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Product *"
                      fullWidth
                      error={!!searchError}
                      helperText={searchError}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">
                        {option.title}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={productSearchTerm ? 'No products found' : 'Start typing to search products'}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Season"
                  value={formData.season}
                  onChange={(e) => handleFormChange('season', e.target.value)}
                  placeholder={selectedProduct ? "Product season" : "Select a product first"}
                  disabled={!selectedProduct || readonlyFields.season}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: selectedProduct ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: selectedProduct ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
                    },
                  }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Discipline"
                  variant="filled"
                  value={formData.discipline}
                  onChange={(e) => handleFormChange('discipline', e.target.value)}
                  placeholder={selectedProduct ? "Product discipline" : "Select a product first"}
                  disabled={!selectedProduct || readonlyFields.discipline}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: selectedProduct ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: selectedProduct ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="UUID"
                  variant="filled"
                  value={formData.uuid}
                  onChange={(e) => handleFormChange('uuid', e.target.value)}
                  placeholder={selectedProduct ? "Product UUID" : "Select a product first"}
                  disabled={!selectedProduct || readonlyFields.uuid}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: selectedProduct ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: selectedProduct ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="EPROF (ISBN)"
                  variant="filled"
                  value={formData.eprof}
                  onChange={(e) => handleFormChange('eprof', e.target.value)}
                  placeholder={selectedProduct ? "Product EPROF/ISBN" : "Select a product first"}
                  disabled={!selectedProduct || readonlyFields.eprof}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: selectedProduct ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: selectedProduct ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.38)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="SVN Repository *"
                  value={formData.svnRepository}
                  onChange={(e) => handleFormChange('svnRepository', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  value={formData.rootFolder}
                  onChange={(event, newValue) => {
                    handleFormChange('rootFolder', newValue || '');
                  }}
                  options={rootFolders}
                  loading={isLoadingRootFolders}
                  disabled={isLoadingRootFolders}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Root Folder *"
                      variant="filled"
                      error={!!rootFoldersError}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: rootFoldersError ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover fieldset': {
                            borderColor: rootFoldersError ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: rootFoldersError ? 'error.main' : 'primary.main',
                          },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingRootFolders ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option, state) => (
                    <Tooltip 
                      title={option} 
                      placement="right"
                      arrow
                      enterDelay={500}
                      leaveDelay={0}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          fontSize: '0.75rem',
                          maxWidth: 300,
                          backgroundColor: 'rgba(97, 97, 97, 0.92)',
                        },
                      }}
                    >
                      <Box component="li" {...props}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          width: '100%',
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: state.selected ? 600 : 400,
                              color: state.selected ? 'primary.main' : 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {option}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Tooltip 
                        title={option} 
                        placement="top"
                        arrow
                        enterDelay={500}
                        leaveDelay={0}
                      >
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            '& .MuiChip-icon': {
                              color: 'inherit',
                            },
                            maxWidth: 150,
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        />
                      </Tooltip>
                    ))
                  }
                  noOptionsText={
                    isLoadingRootFolders 
                      ? 'Loading root folders...' 
                      : rootFoldersError 
                        ? 'Error loading root folders'
                        : 'No root folders found'
                  }
                  loadingText="Loading root folders..."
                  filterOptions={(options, { inputValue }) => {
                    const filtered = options.filter((option) =>
                      option.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    return filtered;
                  }}
                />
                {rootFoldersError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {rootFoldersError}
                  </Typography>
                )}
                {!isLoadingRootFolders && !rootFoldersError && rootFolders.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {rootFolders.length} root folder{rootFolders.length !== 1 ? 's' : ''} available
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  value={selectedCaseOwner}
                  options={caseOwnerOptions}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.name
                  }
                  inputValue={caseOwnerSearchTerm}
                  onInputChange={(event, newInputValue) => {
                    setCaseOwnerSearchTerm(newInputValue);
                    handleFormChange('caseOwner', newInputValue);
                  }}               
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedCaseOwner(newValue);
                      setCaseOwnerSearchTerm(newValue.name);
                      handleFormChange('caseOwner', newValue.name);
                    } else {
                      setSelectedCaseOwner(null);
                      setCaseOwnerSearchTerm('');
                      handleFormChange('caseOwner', '');
                    }
                  }}
                  loading={isSearchingCaseOwner}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Case Owner *"
                      variant="filled"
                      fullWidth
                      error={!!caseOwnerSearchError}
                      helperText={caseOwnerSearchError}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearchingCaseOwner ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">
                        {option.name}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={caseOwnerSearchTerm ? 'No users found' : 'Start typing to search users'}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Vendor(s) Data Section */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Vendor(s) Data *
            </Typography>
            {vendors.map((vendor, index) => (
              <Box
                key={vendor.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9',
                }}
              >
                <IconButton
                  data-testid="delete"
                  disableRipple
                  onClick={() => handleRemoveVendor(vendor.id)}
                  color="error"
                >
                  <RemoveCircleRoundedIcon />
                  <Typography sx={{ ml: 2 }} color={'text.secondary'}>
                    Vendor
                  </Typography>
                </IconButton>

                <Grid container spacing={4} sx={{ flex: 1 }}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Vendor"
                      value={vendors[index].vendor}
                      onChange={(e) => {
                        const updatedVendors = [...vendors];
                        updatedVendors[index].vendor = e.target.value;
                        setVendors(updatedVendors);
                      }}
                      error={hasSpecialCharacters(vendors[index].vendor)}
                      helperText={
                        hasSpecialCharacters(vendors[index].vendor) 
                          ? 'Only letters, numbers, hyphens, underscores, and periods allowed'
                          : ''
                      }
                      sx={{ backgroundColor: '#f5f5f5' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                      <InputLabel>Question Restriction</InputLabel>
                      <Select
                        value={vendors[index].questionRestriction || questionRestrictionOptions[0].value}
                        variant="filled"
                        label="Question Restriction"
                        onChange={(e) => {
                          if (e.target.value) {
                            const updatedVendors = [...vendors];
                            updatedVendors[index].questionRestriction = e.target.value;
                            setVendors(updatedVendors);
                          }
                        }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      >
                        {questionRestrictionOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                      <InputLabel>Question Type</InputLabel>
                      <Select
                        value={vendors[index].questionType || WAS_QUESTION_TYPE_MAP[0].value.toString()}
                        variant="filled"
                        label="Question Type"
                        onChange={(e) => {
                          if (e.target.value) {
                            const updatedVendors = [...vendors];
                            updatedVendors[index].questionType = e.target.value;
                            setVendors(updatedVendors);
                          }
                        }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      >
                        {WAS_QUESTION_TYPE_MAP.slice().sort((a, b) => {
                          if (a.value === -2) return -1;
                          if (b.value === -2) return 1;
                          return a.label.localeCompare(b.label);
                        }).map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button
                onClick={handleAddVendor}
                startIcon={<AddCircleIcon color="primary" />}
                variant="text"
                color="inherit"
              >
                <Typography variant="body2">
                  Add Exception / Restriction
                </Typography>
              </Button>
            </Box>
          </Box>

          {/* Assignee(s) Data Section */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Assignee(s) Data *
            </Typography>
            {assignees.map((assignee, index) => (
              <Box
                key={assignee.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9',
                }}
              >
                <IconButton
                  data-testid="delete"
                  disableRipple
                  onClick={() => handleRemoveAssignee(assignee.id)}
                  color="error"
                >
                  <RemoveCircleRoundedIcon />
                  <Typography sx={{ ml: 2 }} color={'text.secondary'}>
                    Assignee
                  </Typography>
                </IconButton>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                  <Grid item xs={12} sm={12}>
                    <Autocomplete
                      options={assigneeOptions[assignee.id] || []}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option.name
                      }
                      inputValue={assigneeSearchTerms[assignee.id] || ''}
                      onInputChange={(event, newInputValue) => {
                        setAssigneeSearchTerms(prev => ({ ...prev, [assignee.id]: newInputValue }));
                        // Debounced search
                        const timeoutId = setTimeout(() => {
                          searchAssignees(newInputValue, assignee.id);
                        }, 300);
                        return () => clearTimeout(timeoutId);
                      }}
                      onChange={(event, newValue) => {
                        const updatedAssignees = [...assignees];
                        if (newValue && typeof newValue === 'object') {
                          updatedAssignees[index].assignee = newValue.name;
                          updatedAssignees[index].assigneeId = newValue.userId;
                          setAssigneeSearchTerms(prev => ({ ...prev, [assignee.id]: newValue.name }));
                        } else {
                          updatedAssignees[index].assignee = '';
                          updatedAssignees[index].assigneeId = '';
                        }
                        setAssignees(updatedAssignees);
                      }}
                      loading={isSearchingAssignee[assignee.id] || false}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assignee"
                          variant="filled"
                          fullWidth
                          sx={{ backgroundColor: '#f5f5f5' }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isSearchingAssignee[assignee.id] ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.name}
                          </Typography>
                        </Box>
                      )}
                      noOptionsText={assigneeSearchTerms[assignee.id] ? 'No users found' : 'Start typing to search users'}
                      isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                      <InputLabel>Question Restriction</InputLabel>
                      <Select
                        value={assignee.questionRestriction || questionRestrictionOptions[0].value}
                        variant="filled"
                        label="Question Restriction"
                        onChange={(e) => {
                          if (e.target.value) {
                            const updatedAssignees = [...assignees];
                            updatedAssignees[index].questionRestriction = e.target.value;
                            setAssignees(updatedAssignees);
                          }
                        }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      >
                        {questionRestrictionOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                      <InputLabel>Question Type</InputLabel>
                      <Select
                        value={assignee.questionType || WAS_QUESTION_TYPE_MAP[0].value.toString()}
                        variant="filled"
                        label="Question Type"
                        onChange={(e) => {
                          if (e.target.value) {
                            const updatedAssignees = [...assignees];
                            updatedAssignees[index].questionType = e.target.value;
                            setAssignees(updatedAssignees);
                          }
                        }}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      >
                        {WAS_QUESTION_TYPE_MAP.slice().sort((a, b) => {
                            if (a.value === -2) return -1;
                            if (b.value === -2) return 1;
                            return a.label.localeCompare(b.label);
                          }).map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              <Typography variant="body2">
                                {option.label}
                              </Typography>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button
                onClick={handleAddAssignee}
                startIcon={<AddCircleIcon color="primary" />}
                variant="text"
                color="inherit"
              >
                <Typography variant="body2">
                  Add Exception / Restriction
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        {submitError && (
          <Typography variant="body2" color="error" sx={{ flexGrow: 1 }}>
            {submitError}
          </Typography>
        )}
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          sx={(theme: Theme) => ({
            color: theme.palette.grey[600],
            textTransform: 'none',
            fontWeight: 'normal',
          })}
        >
          CANCEL
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSubmitting}
          variant="contained"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            textTransform: 'none',
            fontWeight: 'normal',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          })}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'SAVING...' : 'SAVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductCreateDialog;
